import React, { useState, useEffect, useRef } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCompany } from '@/features/multi-company';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';

interface CompanySelectorProps {
  variant?: 'default' | 'compact';
  className?: string;
}

export const CompanySelector: React.FC<CompanySelectorProps> = ({
  variant = 'default',
  className,
}) => {
  const { currentCompany, availableCompanies, switchCompany, isLoading, refreshCompanyList } = useCompany();
  const [open, setOpen] = useState(false);
  const refreshCompanyListRef = useRef(refreshCompanyList);
  refreshCompanyListRef.current = refreshCompanyList;

  // Refresh company list after the popover has fully opened
  useEffect(() => {
    if (open) {
      refreshCompanyListRef.current();
    }
  }, [open]);

  const handleSelect = async (companyId: string) => {
    if (companyId === currentCompany?.id) {
      setOpen(false);
      return;
    }

    try {
      await switchCompany(companyId);
      setOpen(false);
    } catch (error) {
      console.error('Failed to switch company:', error);
    }
  };

  if (!currentCompany) {
    return null;
  }

  const isCompact = variant === 'compact';

  // Single company - just display it
  if (availableCompanies.length <= 1) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 rounded-md bg-gradient-to-r from-primary/15 to-primary/10 border-2 border-primary/30 shadow-sm',
          isCompact ? 'px-2 py-1' : 'px-2.5 py-1',
          className
        )}
      >
        <Building2 className={cn('text-primary', isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
        <span className={cn('font-bold text-primary', isCompact ? 'text-xs' : 'text-xs')}>
          {currentCompany.name}
        </span>
      </div>
    );
  }

  // Multiple companies - show selector
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-auto rounded-md bg-gradient-to-r from-primary/15 to-primary/10 border-2 border-primary/30 shadow-sm hover:shadow-md transition-all duration-200 hover:bg-primary/20',
            isCompact ? 'py-1 px-2' : 'py-1 px-2.5',
            className
          )}
          disabled={isLoading}
        >
          <div className="flex items-center gap-1.5">
            <Building2 className={cn('text-primary', isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            <span className={cn('font-bold text-primary', isCompact ? 'text-xs' : 'text-xs')}>
              {currentCompany.name}
            </span>
            <ChevronsUpDown className={cn('text-primary/70', isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search company..." className="h-9" />
          <CommandEmpty>No company found.</CommandEmpty>
          <CommandGroup>
            {availableCompanies.map((company) => (
              <CommandItem
                key={company.id}
                value={company.id}
                onSelect={() => handleSelect(company.id)}
                className="cursor-pointer"
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    currentCompany.id === company.id ? 'opacity-100 text-primary' : 'opacity-0'
                  )}
                />
                <div className="flex items-center gap-2 flex-1">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm">{company.name}</span>
                    <span className="text-xs text-muted-foreground">Code: {company.code}</span>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
